import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { toast } from "sonner";
import { Search, Eye, CheckCircle2 } from "lucide-react";

export default function AdminAgents() {
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");
  const [activatingId, setActivatingId] = useState(null);

  const load = async (query = "") => {
    const { data } = await api.get("/admin/agents", { params: { q: query } });
    setAgents(data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/agents/${id}`, { status });
    toast.success(`Agent ${status}`);
    load(q);
  };

  const activateOffline = async (a) => {
    if (!window.confirm(`Activate ${a.agent_name || a.email} on ${a.requested_plan_name || a.requested_plan || 'default plan'}?\nThis records the offline payment and sends a confirmation email.`)) return;
    setActivatingId(a.id);
    try {
      await api.post(`/admin/agents/${a.id}/activate-offline`, {});
      toast.success("Agent activated — email sent");
      load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate");
    } finally {
      setActivatingId(null);
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar role="admin" />
      <main className="flex-1 bg-[#FCFCFC] p-8 lg:p-12">
        <div className="max-w-6xl">
          <div className="overline">Directory</div>
          <h1 className="font-editorial text-4xl mt-2">Travel Agents</h1>

          <div className="mt-6 flex gap-3">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-3 text-slate-400" />
              <input data-testid="admin-agents-search" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>{ if (e.key==="Enter") load(q); }}
                placeholder="Search by Agent ID, name, agency, mobile, email…" className="w-full pl-9 pr-3 py-2.5 border border-border rounded text-sm bg-white" />
            </div>
            <button data-testid="admin-agents-filter-btn" onClick={()=>load(q)} className="text-xs uppercase tracking-widest px-4 py-2 border border-border rounded hover:border-[#16A34A]">Filter</button>
          </div>

          <div className="mt-6 bg-white border border-border rounded">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3">Agent ID</th>
                  <th className="text-left px-6 py-3">Name / Agency</th>
                  <th className="text-left px-6 py-3">Contact</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {agents.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-slate-400">No agents.</td></tr>}
                {agents.map((a) => (
                  <tr key={a.id} className="border-t border-border hover:bg-slate-50">
                    <td className="px-6 py-3 font-mono-e text-xs">{a.agent_id || "—"}</td>
                    <td className="px-6 py-3">
                      <div className="font-medium">{a.agent_name}</div>
                      <div className="text-xs text-slate-500">{a.agency_name}</div>
                    </td>
                    <td className="px-6 py-3">
                      <div className="text-xs">{a.email}</div>
                      <div className="text-xs text-slate-500">{a.mobile}</div>
                    </td>
                    <td className="px-6 py-3">
                      <StatusPill status={a.status} />
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {a.status === "suspended" ? (
                          <button data-testid={`agent-reactivate-${a.id}`} onClick={()=>updateStatus(a.id, "active")} className="text-xs uppercase tracking-widest text-emerald-700 px-2">Reactivate</button>
                        ) : (
                          <button data-testid={`agent-suspend-${a.id}`} onClick={()=>updateStatus(a.id, "suspended")} className="text-xs uppercase tracking-widest text-red-600 px-2">Suspend</button>
                        )}
                        {(a.status === "pending_activation" || a.status === "pending_payment") && (
                          <button
                            data-testid={`agent-activate-${a.id}`}
                            onClick={() => activateOffline(a)}
                            disabled={activatingId === a.id}
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-white bg-[#16A34A] hover:bg-emerald-700 disabled:opacity-60 px-2.5 py-1 rounded"
                          >
                            <CheckCircle2 size={12}/> {activatingId === a.id ? "…" : "Activate"}
                          </button>
                        )}
                        <Link data-testid={`agent-view-${a.id}`} to={`/admin/agents/${a.id}`} className="p-2 hover:text-[#16A34A]"><Eye size={14}/></Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
    pending_activation: "bg-amber-50 text-amber-700 border-amber-200",
    suspended: "bg-red-50 text-red-700 border-red-200",
  };
  return <span className={`text-xs px-2 py-0.5 rounded border ${map[status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{status}</span>;
}
